// Public RFID service page (no login). This is what each lift's RFID sticker resolves to:
// scanned on a phone, at the lift, by an engineer or a building manager.
//
// Session ACG rebuild, amended by Session ADY (Director decision, 17/07/26): the DEFAULT view
// fits one mobile viewport (iPhone 14, 390x844; ~780px of safe content height) -- ELE number,
// site, status, service dates, LOLER, the recent history and the call button all resolve in
// one glance. Session ADY lifts the ACG hard no-scroll/no-disclosure lock ONE notch: when more
// visits are on record than the recent rows show, a single expand control reveals the FULL
// service history in place (the page then scrolls -- deliberate; the Director wants every
// historic service sitting on the tag). Everything is a single flex column; the history block
// flexes to absorb leftover space so the call button always sits at the bottom without a gap.
// The skyline footer strip is DELIBERATELY absent on this page only (a documented exception to
// the shared footer convention -- it costs ~100px of the one-screen budget; portal + emails
// keep theirs).
//
// Colour logic (Session ACG): reference data is BLUE, so the ELE number renders in the
// dedicated #2CB9F3 token (text-ele-loler; was white). The call CTA is brand green with DARK
// text (the locked email-CTA convention). The LOLER strip border is a pass/fail safety
// signal (green when clear, blue advisory, red defect -- the canonical LOLER mapping), never
// purple. Compliance status keeps the existing green/amber/red mapping.
//
// Design constraint (the most important one): assume zero lift knowledge and zero code
// knowledge. Plain English, branded, mobile-first. House rules: no double hyphens on screen,
// no personal names (the one exception: an attending engineer's FIRST name in the service
// history, a standing Director decision), "RFID" never "NFC", the single client phone
// 0203 576 4760.
//
// Styling note: brand colours are CSS variables, so Tailwind alpha modifiers (bg-ele-green/10)
// do NOT apply; tints use explicit rgba arbitrary values.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PublicNfc, LolerStateKey, ComplianceKey } from "../../lib/api";
import JobTypeBadge from "../../components/JobTypeBadge";
import { FullScreenLoader } from "../../components/Loader";
import { EngineerLogVisit } from "./EngineerLogVisit";

const PHONE = "0203 576 4760";
const PHONE_TEL = "02035764760";

// Compliance status -> tinted pill styling (the existing green / amber / red mapping; the
// app-side amber stays Tailwind's amber-400 as on the office register -- the brand palette
// has no dark-surface amber token).
const COMPLIANCE_STYLE: Record<ComplianceKey, { border: string; text: string; dot: string; bg: string }> = {
  green: { border: "border-ele-green", text: "text-ele-green", dot: "bg-ele-green", bg: "bg-[rgba(99,196,31,0.08)]" },
  amber: { border: "border-amber-400", text: "text-amber-400", dot: "bg-amber-400", bg: "bg-[rgba(251,191,36,0.08)]" },
  red: { border: "border-ele-danger", text: "text-ele-danger", dot: "bg-ele-danger", bg: "bg-[rgba(255,90,90,0.08)]" },
};

// LOLER state -> strip styling (canonical mapping: clear green, advisory blue, defect the
// functional danger token).
const LOLER_STYLE: Record<LolerStateKey, { border: string; text: string }> = {
  clear: { border: "border-ele-green", text: "text-ele-green" },
  advisory: { border: "border-ele-blue", text: "text-ele-blue" },
  defect: { border: "border-ele-danger", text: "text-ele-danger" },
};

export function NfcServicePage() {
  const { token = "" } = useParams();
  const [record, setRecord] = useState<PublicNfc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"not_found" | "error" | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.publicNfc
      .get(token)
      .then((r) => {
        if (live) setRecord(r);
      })
      .catch((err) => {
        if (!live) return;
        setError(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  if (loading) return <FullScreenLoader />;

  if (error || !record) {
    return (
      <Shell>
        <HeaderBand eleNumber="Lift record" description={null} />
        <div className="flex-1 rounded-[7px] border border-ele-purple-accent bg-black/20 px-4 py-5 text-center">
          <h1 className="mb-1 font-heading text-xl text-ele-white">
            {error === "not_found" ? "We could not find this lift" : "Something went wrong"}
          </h1>
          <p className="text-sm text-ele-body">
            {error === "not_found"
              ? "This sticker may be damaged, or this lift is not on our system yet. Call us and we will help straight away."
              : "Please try again in a moment. If it keeps happening, call us and we will help."}
          </p>
        </div>
        <CallButton />
        <LegalFooter />
      </Shell>
    );
  }

  const description = record.liftType && record.liftType !== record.assetType ? `${record.assetType} · ${record.liftType}` : record.assetType;

  return (
    <Shell>
      <HeaderBand eleNumber={record.eleNumber} description={description} />

      {(record.siteName || record.siteAddress) && (
        <div className="rounded-[7px] border border-[rgba(125,33,199,0.3)] bg-black/20 px-3 py-2">
          {record.siteName && <p className="text-xs font-semibold text-ele-white">{record.siteName}</p>}
          {record.siteAddress && <p className="text-[11px] text-ele-body opacity-70">{record.siteAddress}</p>}
        </div>
      )}

      <StatusPill compliance={record.compliance} />

      <dl className="grid grid-cols-2 gap-2">
        <Fact label="Last service" value={record.lastServiceDisplay ?? "Not recorded yet"} />
        <Fact label="Next due" value={record.nextServiceDisplay ?? "To be scheduled"} overdue={record.nextServiceOverdue} />
      </dl>

      <LolerStrip loler={record.loler} />

      <ServiceHistory visits={record.serviceHistory} />

      <CallButton />
      <EngineerLogVisit token={token} />
      <LegalFooter />
    </Shell>
  );
}

// One flex column filling the viewport: content stacks with a tight, even gap and the
// history block absorbs any leftover height, so nothing needs to scroll on a 390x844 phone.
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-ele-bg px-4 py-3 text-ele-body">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-2">{children}</div>
    </div>
  );
}

// Header band: identity left (eyebrow, blue ELE number, small description), brand mark right.
function HeaderBand({ eleNumber, description }: { eleNumber: string; description: string | null }) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-menu text-[9px] uppercase tracking-[0.18em] text-ele-loler">RFID service record</p>
        <h1 className="font-heading text-[33px] font-bold leading-tight text-ele-loler">{eleNumber}</h1>
        {description && <p className="text-[11px] text-ele-body opacity-70">{description}</p>}
      </div>
      <img src="/brand/ele-logo.png" alt="East London Elevators" className="h-9 w-auto flex-none" />
    </header>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="font-menu text-[9px] uppercase tracking-wider text-ele-loler">{children}</p>;
}

// The compliance status as one tinted pill: dot + label on a line, one line of plain English
// under it.
function StatusPill({ compliance }: { compliance: PublicNfc["compliance"] }) {
  const style = COMPLIANCE_STYLE[compliance.status];
  return (
    <div className={`rounded-lg border px-3 py-2 ${style.border} ${style.bg}`}>
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${style.dot}`} />
        <span className={`font-heading text-[15px] font-bold uppercase tracking-wide ${style.text}`}>{compliance.label}</span>
      </div>
      <p className="mt-0.5 text-[11px] text-ele-body opacity-75">{compliance.detail}</p>
    </div>
  );
}

function Fact({ label, value, overdue = false }: { label: string; value: string; overdue?: boolean }) {
  return (
    <div className={`rounded-[7px] border bg-black/25 px-2.5 py-2 ${overdue ? "border-ele-danger" : "border-[rgba(125,33,199,0.3)]"}`}>
      <dt className="font-menu text-[9px] uppercase tracking-wider text-ele-loler">{label}</dt>
      <dd className="text-[13px] font-semibold text-ele-white">
        {value}
        {overdue && <span className="ml-1.5 font-heading text-[11px] uppercase tracking-wide text-ele-danger">Overdue</span>}
      </dd>
    </div>
  );
}

// The LOLER safety inspection as a single compact strip. The border is the pass/fail signal
// (canonical LOLER colours), never purple; the expiry sits right-aligned.
function LolerStrip({ loler }: { loler: PublicNfc["loler"] }) {
  if (!loler) {
    return (
      <div className="rounded-[7px] border border-white/10 bg-black/20 px-3 py-2">
        <SectionLabel>Safety inspection</SectionLabel>
        <p className="text-[13px] text-ele-white">No inspection recorded yet</p>
      </div>
    );
  }
  const style = LOLER_STYLE[loler.state];
  return (
    <div className={`flex items-center justify-between gap-3 rounded-[7px] border bg-black/20 px-3 py-2 ${style.border}`}>
      <div className="min-w-0">
        <SectionLabel>Safety inspection</SectionLabel>
        <p className={`truncate font-heading text-sm font-bold uppercase tracking-wide ${style.text}`}>{loler.label}</p>
      </div>
      {loler.expiryDisplay && (
        <p className="flex-none text-right text-[11px] text-ele-body opacity-75">
          Valid until
          <br />
          <span className="font-bold text-ele-white">{loler.expiryDisplay}</span>
        </p>
      )}
    </div>
  );
}

// NB there is deliberately NO open-defects section on this page (Session ADY, Director decision):
// a person standing in the lift must never read a fault list about it. Defects inform the
// compliance status server-side; the detail never reaches this page or its payload.

// Service history (Session ADY, Director decision superseding the ACG "no expand/collapse" lock):
// the DEFAULT view stays clean -- the 4 most recent visits, fitting a phone screen -- and when
// more are on record, ONE full-width control expands the complete history in place (the page
// scrolls; never a second page the tag could not reach). A service visit that completed with no
// faults recorded shows a small green tick ("Service successful"); a visit that was NOT clean
// renders NEUTRALLY -- the date and the visit type, no verdict tick, and never the reason
// (honesty rule: never green-tick an unclean visit, never show the public why it was unclean).
// Type tag colour via the canonical shared jobType helper; the attending engineer's FIRST name
// stays (standing Director decision), inline and small. flex-1 lets this block absorb leftover
// viewport height so the call button never floats.
const RECENT_VISIT_ROWS = 4;

function VisitRow({ visit: v }: { visit: PublicNfc["serviceHistory"][number] }) {
  const isLoler = v.typeKey === "loler";
  const successful = v.typeKey === "service" && v.clean === true;
  return (
    <li className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1.5 last:border-0">
      <span className="min-w-0 truncate">
        <JobTypeBadge type={v.typeKey} size="sm" label={v.type} />
        {successful && (
          <span className="ml-1.5 text-[11px] font-bold text-ele-green" role="img" aria-label="Service successful" title="Service successful">
            ✓
          </span>
        )}
        {!isLoler && v.engineerFirstName && (
          <span className="ml-1.5 text-[10px] text-ele-body opacity-60">· {v.engineerFirstName}</span>
        )}
      </span>
      <span className="flex-none text-[11px] text-ele-body opacity-65">{v.dateDisplay}</span>
    </li>
  );
}

function ServiceHistory({ visits }: { visits: PublicNfc["serviceHistory"] }) {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? visits : visits.slice(0, RECENT_VISIT_ROWS);
  return (
    <div className="flex-1 rounded-[7px] border border-[rgba(125,33,199,0.3)] bg-black/20 px-3 py-2">
      <SectionLabel>Service history</SectionLabel>
      {rows.length === 0 ? (
        <p className="mt-1 text-[11px] text-ele-body opacity-75">No completed visits are on record for this lift yet.</p>
      ) : (
        <ul className="mt-1">
          {rows.map((v, i) => (
            <VisitRow key={i} visit={v} />
          ))}
        </ul>
      )}
      {visits.length > RECENT_VISIT_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className="mt-1.5 flex min-h-[44px] w-full items-center justify-center rounded-[7px] border border-white/10 bg-black/25 px-3 text-[12px] font-semibold text-ele-loler transition hover:bg-white/5"
        >
          {expanded ? "Show recent visits" : `Full service history (${visits.length} visits)`}
        </button>
      )}
    </div>
  );
}

// One-tap emergency call: brand green with DARK text (the locked CTA convention), never
// compressed (flex-none) and always visible without scrolling.
function CallButton() {
  return (
    <a
      href={`tel:${PHONE_TEL}`}
      className="flex w-full flex-none items-center justify-center gap-2.5 rounded-lg bg-ele-green px-5 py-[15px] font-heading text-[19px] font-bold uppercase tracking-wide text-ele-bg transition hover:brightness-110"
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-current">
        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
      </svg>
      Call ELE {PHONE}
    </a>
  );
}

// A single-line legal footer, pipe-separated. NO skyline strip on this page (deliberate
// one-screen exception, see the file header). Below it, a muted STATIC link to the customer
// portal (Session AFH); the href is a fixed literal, never built from record data.
function LegalFooter() {
  return (
    <footer className="flex-none pb-0.5 pt-1 text-center text-ele-body">
      <p className="text-[9px] opacity-35">East London Elevators Ltd | {PHONE} | info@eastlondonelevators.co.uk</p>
      <a href="/portal/login" className="mt-1 inline-block font-body text-xs opacity-40 transition hover:opacity-70">
        View your full account - client portal
      </a>
    </footer>
  );
}
